import React from 'react';
import { AnyAction } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ReactExport from 'react-export-excel';

import useAuthHook from '@giro/shared-hooks/useAuth.hook';

import ReportECFilters from './ReportECFilters.component';
import ReportECSummary from './ReportECSummary.component';
import TableReportEC from './TableReportEC.component';
import {
  createReportECFilters,
  formatReportDate,
  getReportAuthorization,
  isSameReportECFilters,
  ReportECFilters as Filters,
  ReportECUsers,
} from './reportEC.utils';

interface Props {
  reports: {
    selector: {
      selectData: (state: any) => any;
      selectLoading: (state: any) => any;
      selectError: (state: any) => any;
      selectFilters: (state: any) => any;
    };
    action: {
      updateFilters: (filters: any) => AnyAction;
      resetState: () => AnyAction;
    };
  };
  receiptAction: (id: string | number) => AnyAction;
  users: ReportECUsers;
  restrictOperator?: boolean;
  showIdentification?: boolean;
  identificationFilters?: boolean;
  exportClientIdentification?: boolean;
}

const ExcelFile = ReactExport.ExcelFile;
const ExcelSheet = ExcelFile.ExcelSheet;
const ExcelColumn = ExcelFile.ExcelColumn;

const ReportEC = ({
  reports,
  receiptAction,
  users,
  restrictOperator = false,
  showIdentification = false,
  identificationFilters = false,
  exportClientIdentification = false,
}: Props) => {
  const dispatch = useDispatch();
  const { user } = useAuthHook();
  const isRestrictedUser = restrictOperator && user?.user?.codes?.idRole === 6;
  const userCode = isRestrictedUser
    ? String(user?.user?.codes?.idUser || '')
    : '';
  const data = useSelector(reports.selector.selectData);
  const loading = Boolean(useSelector(reports.selector.selectLoading));
  const error = useSelector(reports.selector.selectError)?.[0];
  const appliedFilters = useSelector(reports.selector.selectFilters);
  const [filters, setFilters] = React.useState<Filters>(() =>
    createReportECFilters(userCode, identificationFilters)
  );

  React.useEffect(() => {
    const defaults = createReportECFilters(userCode, identificationFilters);
    setFilters(defaults);
    dispatch(
      reports.action.updateFilters({
        ...defaults,
        type: 'ec',
        offset: 0,
        limit: 10,
      })
    );
    return () => {
      dispatch(reports.action.resetState());
    };
  }, [dispatch, userCode, identificationFilters, reports]);

  const pendingFilters = !isSameReportECFilters(filters, appliedFilters);
  const rows = data?.results || [];
  const metadata = data?.metadata;
  const limit = Number(appliedFilters?.limit) || 10;
  const offset = Number(appliedFilters?.offset) || 0;
  const exportColumns = [
    {
      label: 'Data transação',
      value: ({ DtTransacao }) => formatReportDate(DtTransacao),
    },
    { label: 'Código de autorização (NSU)', value: getReportAuthorization },
    { label: 'Valor transação', value: 'VlTransacao' },
    { label: 'Valor líquido', value: 'VlLiberado' },
    { label: 'Tipo', value: 'TpPagamento' },
    { label: 'Parcelas', value: 'QtdParcelas' },
    { label: 'Valor taxas', value: 'VlTaxas' },
    { label: 'Status', value: 'Status' },
    ...(showIdentification || exportClientIdentification
      ? [{ label: 'Identificação do cliente', value: 'DsIdentificacaoCliente' }]
      : []),
    ...(showIdentification
      ? [{ label: 'Identificação do serviço', value: 'DsIdentificacaoServico' }]
      : []),
  ];

  const applyFilters = (nextFilters = filters) => {
    dispatch(
      reports.action.updateFilters({
        ...nextFilters,
        userCode: isRestrictedUser ? userCode : nextFilters.userCode,
        type: 'ec',
        offset: 0,
      })
    );
  };
  const resetFilters = () => {
    const defaults = createReportECFilters(userCode, identificationFilters);
    setFilters(defaults);
    applyFilters(defaults);
  };
  const refresh = () => dispatch(reports.action.updateFilters({}));

  return (
    <Box display="flex" flexDirection="column" gap={1.5} width="100%">
      <Box
        display="flex"
        flexDirection={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        gap={1.5}
      >
        <Box>
          <Typography component="h1" sx={{ fontSize: 24, fontWeight: 800 }}>
            Relatório de transações
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Consulte as vendas do seu estabelecimento, taxas e comprovantes.
          </Typography>
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={refresh}
            disabled={loading}
            sx={{ minHeight: 40, borderColor: 'divider', fontWeight: 700 }}
          >
            Atualizar
          </Button>
          <ExcelFile
            filename="Relatório de transações"
            element={
              <Button
                size="small"
                variant="contained"
                startIcon={<FileDownloadIcon />}
                disabled={loading || error || !rows.length || pendingFilters}
                sx={{ minHeight: 40, fontWeight: 700 }}
              >
                Exportar página
              </Button>
            }
          >
            <ExcelSheet data={rows} name="Transações">
              {exportColumns.map((column) => (
                <ExcelColumn
                  key={column.label}
                  label={column.label}
                  value={column.value}
                />
              ))}
            </ExcelSheet>
          </ExcelFile>
        </Box>
      </Box>
      <ReportECSummary metadata={metadata} loading={loading} />
      <ReportECFilters
        filters={filters}
        onChange={setFilters}
        onSearch={() => applyFilters()}
        onReset={resetFilters}
        loading={loading}
        pending={pendingFilters}
        isRestrictedUser={isRestrictedUser}
        users={users}
        identificationFilters={identificationFilters}
      />
      <TableReportEC
        rows={rows}
        count={Number(metadata?.count) || 0}
        loading={loading}
        error={Boolean(error)}
        limit={limit}
        page={Math.floor(offset / limit)}
        onRetry={refresh}
        onReset={resetFilters}
        onReceipt={(id) => dispatch(receiptAction(id))}
        showIdentification={showIdentification}
        onPageChange={(page) =>
          dispatch(reports.action.updateFilters({ offset: page * limit }))
        }
        onLimitChange={(nextLimit) =>
          dispatch(
            reports.action.updateFilters({ limit: nextLimit, offset: 0 })
          )
        }
      />
    </Box>
  );
};

export default ReportEC;
